import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { ErrorBlock } from '@shared/ui/error-modal';
import { Button, Dropdown, Modal } from '@shared/ui/kit';
import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { typography } from '@shared/tokens/typography.stylex';
import { SuccessIcon } from '@shared/assets/icons';
import { useUpdateTickets } from '@pages/event/api/useUpdateTickets';
import { ISegment } from '@pages/event/api/useFetchTicketSegment';

interface ModalMoveToSegmentProps {
  active: boolean;
  closeHandle: () => void;
  segments: ISegment[];
  selectedTickets?: number[];
  onSuccess?: () => void;
  refetchSegments?: () => void;
}
export const ModalMoveToSegment: FC<ModalMoveToSegmentProps> = ({
  active,
  closeHandle,
  segments,
  selectedTickets = [],
  onSuccess,
  refetchSegments,
}) => {
  const [selectedSegment, setSelectedSegment] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');
  const { updateTickets } = useUpdateTickets();

  const handleClose = () => {
    setStatus('form');
    setSelectedSegment(undefined);
    closeHandle();
    if (status === 'success') {
      onSuccess?.();
    }
  };

  const handleMoveParticipants = async () => {
    if (!selectedSegment || selectedTickets.length === 0) {
      setErrorMessage('Выберите сегмент и участников для перемещения');
      setStatus('error');
      return;
    }

    try {
      const result = await updateTickets({
        ticketIds: selectedTickets,
        ticketSegmentId: selectedSegment,
      });

      if (result.data?.updateTickets) {
        if (result.data.updateTickets.rowsAffected === 0) {
          setErrorMessage('Не удалось переместить участников');
          setStatus('error');
          return;
        }

        setStatus('success');
        refetchSegments?.();
        onSuccess?.();
      } else if (result.error) {
        setErrorMessage(result.error.message || 'Ошибка при перемещении участников');
        setStatus('error');
      } else {
        setErrorMessage('Произошла непредвиденная ошибка');
        setStatus('error');
      }
    } catch (error) {
      setErrorMessage('Произошла непредвиденная ошибка');
      setStatus('error');
    }
  };

  const getSelectedSegmentName = () => {
    const segment = segments.find((s) => s.id === selectedSegment);
    return segment?.title || 'выбранный сегмент';
  };
  const selectedSegmentData = segments.find((segment) => segment.id === selectedSegment);
  const renderModalContent = () => {
    if (status === 'success') {
      return (
        <div {...stylex.props(styles.container)}>
          <p {...stylex.props(styles.header)}>Переместить в сегмент</p>
          <div {...stylex.props(styles.successInfo)}>
            <div {...stylex.props(styles.icon)}>
              <SuccessIcon />
            </div>
            <p {...stylex.props(styles.description)}>
              Участники мероприятия успешно перемещены в сегмент {getSelectedSegmentName()}
            </p>
          </div>
          <div {...stylex.props(styles.action)}>
            <Button
              onClick={handleClose}
              variant="accent"
              size="small"
              settings={{ padding: '8px 20px', fontSize: typography['fontSizeCaption-1'] }}
            >
              Закрыть
            </Button>
          </div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <ErrorBlock
          description={errorMessage || 'Произошла непредвиденная ошибка. Попробуйте повторить операцию позднее'}
          onClose={handleClose}
        />
      );
    }

    return (
      <div {...stylex.props(styles.container)}>
        <p {...stylex.props(styles.header)}>Переместить в сегмент</p>
        <Dropdown<ISegment>
          labelMessage="Сегмент *"
          settings={{ width: '100%' }}
          placeholder="Выбрать сегмент"
          options={segments}
          itemText="title"
          itemValue="id"
          value={selectedSegmentData}
          changeFunc={(val) => {
            setSelectedSegment(Number(val[0]));
          }}
        />
        <div {...stylex.props(styles.action, styles.actionCopyParticipants)}>
          <Button
            onClick={handleClose}
            variant="secondary"
            size="small"
            settings={{ padding: '8px 20px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleMoveParticipants}
            variant="accent"
            size="small"
            settings={{ padding: '8px 20px', fontSize: typography['fontSizeCaption-1'] }}
          >
            Добавить
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      {createPortal(
        <Modal
          active={active}
          settingsContainer={{ height: 'auto', minWidth: '480px', padding: '0px 24px' }}
          settingsHeader={{ height: '16px', padding: '0px' }}
          settingsFooter={{ height: '16px', padding: '0px' }}
        >
          {renderModalContent()}
        </Modal>,
        document.getElementById('modal') as Element
      )}
    </>
  );
};
